import React, { createContext, useContext, useState, useEffect } from 'react';

const LocationContext = createContext();

export const LOCATIONS = {
  monopoli: {
    id: 'monopoli',
    name: 'Monopoli',
    tone: 'casual',
    tagline: {
      en: 'Lively beaches, boats & local flavors',
      it: 'Spiagge vivaci, barche e sapori locali',
      fr: 'Plages animées, bateaux et saveurs locales',
      de: 'Lebhafte Strände, Boote und lokale Aromen',
      es: 'Playas animadas, barcos y sabores locales'
    },
    focus: ['beach', 'boat', 'food', 'nightlife'],
    whatsapp: '393331234567'
  },
  polignano: {
    id: 'polignano',
    name: 'Polignano a Mare',
    tone: 'iconic',
    tagline: {
      en: 'Dramatic cliffs, cliff diving & Domenico Modugno',
      it: 'Scogliere spettacolari, tuffi e Domenico Modugno',
      fr: 'Falaises dramatiques, plongeons et Domenico Modugno',
      de: 'Dramatische Klippen, Klippenspringen und Domenico Modugno',
      es: 'Acantilados dramáticos, saltos y Domenico Modugno'
    },
    focus: ['cliff-diving', 'photography', 'romantic', 'iconic'],
    whatsapp: '393331234568'
  },
  fasano: {
    id: 'fasano',
    name: 'Fasano',
    tone: 'premium',
    tagline: {
      en: 'Luxury masserie, private chefs & exclusive clubs',
      it: 'Masserie di lusso, chef privati e club esclusivi',
      fr: 'Masserie de luxe, chefs privés et clubs exclusifs',
      de: 'Luxus-Masserie, Privatköche und exklusive Clubs',
      es: 'Masserías de lujo, chefs privados y clubs exclusivos'
    },
    focus: ['luxury', 'masseria', 'fine-dining', 'wellness'],
    whatsapp: '393339876543'
  }
};

export const GUEST_TYPES = {
  couple: {
    id: 'couple',
    label: { en: 'Couple', it: 'Coppia', fr: 'Couple', de: 'Paar' },
    icon: '💑',
    preferences: ['romantic', 'sunset', 'quiet', 'intimate']
  },
  family: {
    id: 'family',
    label: { en: 'Family', it: 'Famiglia', fr: 'Famille', de: 'Familie' },
    icon: '👨‍👩‍👧‍👦',
    preferences: ['family-friendly', 'safe', 'easy-access', 'activities']
  },
  luxury: {
    id: 'luxury',
    label: { en: 'Luxury', it: 'Lusso', fr: 'Luxe', de: 'Luxus' },
    icon: '✨',
    preferences: ['premium', 'exclusive', 'fine-dining', 'private']
  },
  budget: {
    id: 'budget',
    label: { en: 'Budget-Friendly', it: 'Economico', fr: 'Économique', de: 'Budgetfreundlich' },
    icon: '🎒',
    preferences: ['free', 'affordable', 'local', 'authentic']
  }
};

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(() => {
    const saved = localStorage.getItem('ospitai-location');
    return saved || null;
  });
  
  const [guestType, setGuestType] = useState(() => {
    const saved = localStorage.getItem('ospitai-guest-type');
    return saved || null;
  });

  const [isOnboarded, setIsOnboarded] = useState(() => {
    return localStorage.getItem('ospitai-onboarded') === 'true';
  });

  const updateLocation = (loc) => {
    setLocation(loc);
    localStorage.setItem('ospitai-location', loc);
  };

  const updateGuestType = (type) => {
    setGuestType(type);
    localStorage.setItem('ospitai-guest-type', type);
  };

  const completeOnboarding = () => {
    setIsOnboarded(true);
    localStorage.setItem('ospitai-onboarded', 'true');
  };

  const resetOnboarding = () => {
    setIsOnboarded(false);
    setLocation(null);
    setGuestType(null);
    localStorage.removeItem('ospitai-onboarded');
    localStorage.removeItem('ospitai-location');
    localStorage.removeItem('ospitai-guest-type');
  };

  const locationData = location ? LOCATIONS[location] : null;
  const guestTypeData = guestType ? GUEST_TYPES[guestType] : null;

  return (
    <LocationContext.Provider value={{
      location,
      guestType,
      locationData,
      guestTypeData,
      isOnboarded,
      updateLocation,
      updateGuestType,
      completeOnboarding,
      resetOnboarding,
      LOCATIONS,
      GUEST_TYPES
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => useContext(LocationContext);
